import { useLocalStorage } from '@sixfold/app-utils';
import { isNil } from '@sixfold/typed-primitives';
import * as React from 'react';

export const ANALYTICS_KEY = 'analytics';
export const SHARED_VIEWS_KEY = 'sharedViews';

export type FeaturesVisited = {
  [ANALYTICS_KEY]?: string;
  [SHARED_VIEWS_KEY]?: string;
};

const parseFeaturesVisited = (raw: string): FeaturesVisited => {
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

export const useFeaturesVisited = () => {
  const [featuresVisitedRaw, setFeaturesVisitedRaw] = useLocalStorage<string>('features-visited', JSON.stringify({}));

  // Store the latest raw value in a ref so the callback can access it without recreating
  const featuresVisitedRawRef = React.useRef(featuresVisitedRaw);
  React.useEffect(() => {
    featuresVisitedRawRef.current = featuresVisitedRaw;
  }, [featuresVisitedRaw]);

  const featuresVisited: FeaturesVisited = React.useMemo(
    () => parseFeaturesVisited(featuresVisitedRaw),
    [featuresVisitedRaw],
  );

  const setFeatureVisited = React.useCallback(
    (feature: keyof FeaturesVisited) => {
      const current = parseFeaturesVisited(featuresVisitedRawRef.current);
      if (isNil(current[feature])) {
        setFeaturesVisitedRaw(JSON.stringify({ ...current, [feature]: new Date().toISOString() }));
      }
    },
    [setFeaturesVisitedRaw],
  );

  return {
    featuresVisited,
    setFeatureVisited,
  };
};
